import { useState } from 'react';
import { Casino } from '../../mocks/casinos';
import { getPlayLinkByBrand, trackRegisterClickByBrand } from '../../config/playLinks';

interface CasinoCardProps {
  casino: Casino;
  rank?: number;
  onReviewClick?: () => void;
  showEditorNote?: boolean;
  showTrustScores?: boolean;
}

export default function CasinoCard({ 
  casino, 
  rank, 
  onReviewClick,
  showEditorNote = false,
  showTrustScores = false
}: CasinoCardProps) {
  const [imageError, setImageError] = useState(false);
  const casinoBrandKey = casino.name
    .toLowerCase()
    .replace(/\sonline casino/g, '')
    .replace(/[^a-z0-9]/g, '');
  const casinoPlayLink = getPlayLinkByBrand(casinoBrandKey);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
          {/* Rank Badge */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 text-white font-bold text-xl rounded-lg shadow-md">
              {casino.rank}
            </div>
          </div>

          {/* Casino Logo */}
          <div className="flex-shrink-0 w-full sm:w-28 h-40 sm:h-28 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
            {!imageError ? (
              <img
                src={casino.logo}
                alt={casino.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="text-gray-400 text-center px-2">
                <i className="ri-image-line text-2xl"></i>
                <div className="text-xs mt-1">{casino.name}</div>
              </div>
            )}
          </div>

          {/* Casino Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{casino.name}</h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`${
                          i < Math.floor(casino.rating)
                            ? 'ri-star-fill text-yellow-500'
                            : 'ri-star-line text-gray-300'
                        } text-sm`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{casino.rating}</span>
                </div>
              </div>
              {casino.featured && (
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">
                  Featured
                </span>
              )}
            </div>

            <p className="text-gray-600 text-sm mb-4">{casino.description}</p>

            {/* Bonus Highlight */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-gift-line text-orange-600"></i>
                <span className="text-xs font-semibold text-orange-900 uppercase">Welcome Bonus</span>
              </div>
              <p className="text-orange-900 font-bold text-base">{casino.bonus}</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">Games</div>
                <div className="text-sm font-semibold text-gray-900">{casino.games}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Min Deposit</div>
                <div className="text-sm font-semibold text-gray-900">{casino.minDeposit}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Payout Speed</div>
                <div className="text-sm font-semibold text-gray-900">{casino.payoutSpeed}</div>
              </div>
            </div>

            {/* Pros List */}
            {casino.pros && casino.pros.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {casino.pros.slice(0, 4).map((pro, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <i className="ri-check-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <a
                href={casinoPlayLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackRegisterClickByBrand(casinoBrandKey)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3.5 px-6 min-h-[44px] rounded-lg transition-colors text-center whitespace-nowrap"
              >
                Visit Casino
              </a>
              <button
                onClick={onReviewClick}
                className="flex-1 bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3.5 px-6 min-h-[44px] rounded-lg border-2 border-gray-300 transition-colors whitespace-nowrap"
              >
                Read Review
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Editor's Note - Only show if enabled */}
      {showEditorNote && casino.editorNote && (
        <div className="px-6 py-4 bg-orange-50 border-t border-orange-200">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full flex-shrink-0">
              <i className="ri-quill-pen-line text-white text-sm"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-orange-900 mb-1">Editor's Note</p>
              <p className="text-sm text-gray-700 italic leading-relaxed">"{casino.editorNote}"</p>
            </div>
          </div>
        </div>
      )}

      {/* Trust Score Breakdown - Only show if enabled */}
      {showTrustScores && casino.trustScores && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-3">Trust Score Breakdown</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Safety</span>
                <span className="text-xs font-semibold text-gray-900">{casino.trustScores.safety}/10</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full" 
                  style={{ width: `${casino.trustScores.safety * 10}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Bonuses</span>
                <span className="text-xs font-semibold text-gray-900">{casino.trustScores.bonuses}/10</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-500 rounded-full" 
                  style={{ width: `${casino.trustScores.bonuses * 10}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Games</span>
                <span className="text-xs font-semibold text-gray-900">{casino.trustScores.games}/10</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 rounded-full" 
                  style={{ width: `${casino.trustScores.games * 10}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Support</span>
                <span className="text-xs font-semibold text-gray-900">{casino.trustScores.support}/10</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full" 
                  style={{ width: `${casino.trustScores.support * 10}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )
    }
    </div>
  );
}